import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth";
import { canSee, firstAllowedPath, screenForLocation } from "./lib/screens";
import Login from "./pages/Login";
import Home from "./pages/Home";
import DashboardsList from "./pages/DashboardsList";
import DashboardView from "./pages/DashboardView";
import Builder from "./pages/Builder";
import DataSources from "./pages/DataSources";
import Datasets from "./pages/Datasets";
import SqlEditor from "./pages/SqlEditor";
import DatabaseExplorer from "./pages/DatabaseExplorer";
import ERPFlowPlanner from "./pages/ERPFlowPlanner";
import AuditLogs from "./pages/AuditLogs";
import Workspaces from "./pages/Workspaces";
import Reports from "./pages/Reports";
import KpiCenter from "./pages/KpiCenter";
import Exports from "./pages/Exports";
import EtlAdmin from "./pages/EtlAdmin";
import ExcelUploads from "./pages/ExcelUploads";
import AskAIDrawer from "./components/AskAIDrawer";
import AIAnalyticsAgent from "./pages/AIAnalyticsAgent";
import CEOCommandCenter from "./pages/CEOCommandCenter";
import MsReport from "./pages/MsReport";
import ShipmentPerformanceDetail from "./pages/ShipmentPerformanceDetail";
import FdaReleaseHoldDetail from "./pages/FdaReleaseHoldDetail";
import PaymentsReceivedPendingDetail from "./pages/PaymentsReceivedPendingDetail";
import PendingShipmentsDetail from "./pages/PendingShipmentsDetail";
import VarietyAnalysisDetail from "./pages/VarietyAnalysisDetail";
import PriceComparisonOverYearsDetail from "./pages/PriceComparisonOverYearsDetail";
import DummyCartonUsageByPeriodDetail from "./pages/DummyCartonUsageByPeriodDetail";
import ConfirmedOrdersProductionStatusDetail from "./pages/ConfirmedOrdersProductionStatusDetail";
import ProductionPlantOrderViewDetail from "./pages/ProductionPlantOrderViewDetail";
import MasterSearchDetail from "./pages/MasterSearchDetail";
import DummyStockPendingContractsDetail from "./pages/DummyStockPendingContractsDetail";
import RmPurchaseValueDetail from "./pages/RmPurchaseValueDetail";
import PurchaseQualityByVarietyDetail from "./pages/PurchaseQualityByVarietyDetail";
import SupplierAntibioticPositiveDetail from "./pages/SupplierAntibioticPositiveDetail";
import BatchAntibioticHistoryDetail from "./pages/BatchAntibioticHistoryDetail";
import HeadOnCyVsFyDetail from "./pages/HeadOnCyVsFyDetail";
import SpeciesAnalysisDetail from "./pages/SpeciesAnalysisDetail";
import HeadOnVsFinishedPlantDetail from "./pages/HeadOnVsFinishedPlantDetail";
import PoDayCodeStatusDetail from "./pages/PoDayCodeStatusDetail";
import SalesPriceAnalysisDetail from "./pages/SalesPriceAnalysisDetail";
import SaleVsActualPriceDetail from "./pages/SaleVsActualPriceDetail";
import ConsumablesUsageByPlantDetail from "./pages/ConsumablesUsageByPlantDetail";
import RmArrivalsByCountDetail from "./pages/RmArrivalsByCountDetail";
import EuFarmCertificatesDetail from "./pages/EuFarmCertificatesDetail";
import FinishedProductVolumeDetail from "./pages/FinishedProductVolumeDetail";
import ShipPromptnessByPlantDetail from "./pages/ShipPromptnessByPlantDetail";
import YearlyDispatchComparisonDetail from "./pages/YearlyDispatchComparisonDetail";
import StockFinanceExposureDetail from "./pages/StockFinanceExposureDetail";
import PhysicalBarcodeStockDetail from "./pages/PhysicalBarcodeStockDetail";
import PackingMaterialContractsDetail from "./pages/PackingMaterialContractsDetail";
import ChemicalStockDetail from "./pages/ChemicalStockDetail";
import FloorBalanceDetail from "./pages/FloorBalanceDetail";
import ChemicalUsedOnDateDetail from "./pages/ChemicalUsedOnDateDetail";
import OrderDeliveryStatusDetail from "./pages/OrderDeliveryStatusDetail";
import UnderProductionByBuyerDetail from "./pages/UnderProductionByBuyerDetail";
import BuyerOrderAnalysisDetail from "./pages/BuyerOrderAnalysisDetail";
import AccessManagement from "./pages/AccessManagement";
import ClientRequirementsCompletion from "./pages/ClientRequirementsCompletion";

function BackToTopButton() {
  const location = useLocation();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = (event) => {
      const target = event.target;
      const scrollTop = target === document || target === document.documentElement || target === document.body
        ? window.scrollY || document.documentElement.scrollTop || document.body.scrollTop
        : target?.scrollTop || 0;
      setVisible(scrollTop > 320);
    };

    window.addEventListener("scroll", handleScroll, true);
    handleScroll({ target: document });
    return () => window.removeEventListener("scroll", handleScroll, true);
  }, [location.pathname]);

  const moveToTop = () => {
    const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    window.scrollTo({ top: 0, left: 0, behavior });
    document.querySelectorAll("#root *").forEach((element) => {
      if (element.scrollTop > 0) element.scrollTo({ top: 0, left: 0, behavior });
    });
  };

  if (!visible) return null;

  return (
    <button
      type="button"
      aria-label="Move to top"
      title="Move to top"
      onClick={moveToTop}
      className="fixed bottom-6 right-6 z-[10001] flex h-11 w-11 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-lg transition hover:-translate-y-0.5 hover:bg-slate-50 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m18 15-6-6-6 6" />
      </svg>
    </button>
  );
}

function Protected({ children }) {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-10 text-[13px]" style={{ color: "var(--c-text-muted)" }}>Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!canSee(user, screenForLocation(loc.pathname, loc.search))) {
    const landing = firstAllowedPath(user);
    if (landing) return <Navigate to={landing} replace />;
    return (
      <div className="p-10 text-[13px]" style={{ color: "var(--c-text-muted)" }}>
        No analytics screens are enabled for your role ({user.access_role_name || "unassigned"}). Contact your administrator.{" "}
        <button type="button" className="underline" onClick={logout}>Logout</button>
      </div>
    );
  }
  return children;
}


function ClientOnlyRedirect({ children }) {
  const { user } = require("./lib/auth").useAuth();
  const { isLockedDashboardUser } = require("./lib/auth");
  const loc = useLocation();

  if (isLockedDashboardUser(user)) {
    const dashId = user.assigned_dashboard_id || (user.allowed_dashboard_ids || [])[0];
    const allowedPath = dashId ? `/dashboards/${dashId}` : "/dashboards";
    if (loc.pathname !== allowedPath) {
      return <Navigate to={allowedPath} replace />;
    }
  }

  return children;
}


function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" />
        <AskAIDrawer />
        <BackToTopButton />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Protected><ClientOnlyRedirect><Home /></ClientOnlyRedirect></Protected>} />
          <Route path="/dashboards" element={<Protected><ClientOnlyRedirect><DashboardsList /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center" element={<Protected><ClientOnlyRedirect><CEOCommandCenter /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/shipment-performance" element={<Protected><ClientOnlyRedirect><ShipmentPerformanceDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/fda-release-hold" element={<Protected><ClientOnlyRedirect><FdaReleaseHoldDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/payments-received-pending" element={<Protected><ClientOnlyRedirect><PaymentsReceivedPendingDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/pending-shipments" element={<Protected><ClientOnlyRedirect><PendingShipmentsDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/variety-analysis" element={<Protected><ClientOnlyRedirect><VarietyAnalysisDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/price-comparison-over-years" element={<Protected><ClientOnlyRedirect><PriceComparisonOverYearsDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/dummy-carton-usage-by-period" element={<Protected><ClientOnlyRedirect><DummyCartonUsageByPeriodDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/confirmed-orders-production-status" element={<Protected><ClientOnlyRedirect><ConfirmedOrdersProductionStatusDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/production-plant-order-view" element={<Protected><ClientOnlyRedirect><ProductionPlantOrderViewDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/master-search" element={<Protected><ClientOnlyRedirect><MasterSearchDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/dummy-stock-pending-contracts" element={<Protected><ClientOnlyRedirect><DummyStockPendingContractsDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/rm-purchase-value" element={<Protected><ClientOnlyRedirect><RmPurchaseValueDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/purchase-quality-by-variety" element={<Protected><ClientOnlyRedirect><PurchaseQualityByVarietyDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/supplier-antibiotic-positive" element={<Protected><ClientOnlyRedirect><SupplierAntibioticPositiveDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/batch-antibiotic-history" element={<Protected><ClientOnlyRedirect><BatchAntibioticHistoryDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/headon-cy-vs-fy" element={<Protected><ClientOnlyRedirect><HeadOnCyVsFyDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/species-analysis" element={<Protected><ClientOnlyRedirect><SpeciesAnalysisDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/headon-vs-finished-plant" element={<Protected><ClientOnlyRedirect><HeadOnVsFinishedPlantDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/po-day-code-status" element={<Protected><ClientOnlyRedirect><PoDayCodeStatusDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/sales-price-analysis" element={<Protected><ClientOnlyRedirect><SalesPriceAnalysisDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/sale-vs-actual-price" element={<Protected><ClientOnlyRedirect><SaleVsActualPriceDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/consumables-usage-by-plant" element={<Protected><ClientOnlyRedirect><ConsumablesUsageByPlantDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/rm-arrivals-by-count" element={<Protected><ClientOnlyRedirect><RmArrivalsByCountDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/eu-farm-certificates" element={<Protected><ClientOnlyRedirect><EuFarmCertificatesDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/finished-product-volume" element={<Protected><ClientOnlyRedirect><FinishedProductVolumeDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/ship-promptness-by-plant" element={<Protected><ClientOnlyRedirect><ShipPromptnessByPlantDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/yearly-dispatch-comparison" element={<Protected><ClientOnlyRedirect><YearlyDispatchComparisonDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/stock-finance-exposure" element={<Protected><ClientOnlyRedirect><StockFinanceExposureDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/physical-barcode-stock" element={<Protected><ClientOnlyRedirect><PhysicalBarcodeStockDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/packing-material-contracts" element={<Protected><ClientOnlyRedirect><PackingMaterialContractsDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/chemical-stock" element={<Protected><ClientOnlyRedirect><ChemicalStockDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/floor-balance" element={<Protected><ClientOnlyRedirect><FloorBalanceDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/chemical-used-on-date" element={<Protected><ClientOnlyRedirect><ChemicalUsedOnDateDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/order-delivery-status" element={<Protected><ClientOnlyRedirect><OrderDeliveryStatusDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/under-production-by-buyer" element={<Protected><ClientOnlyRedirect><UnderProductionByBuyerDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/buyer-order-analysis" element={<Protected><ClientOnlyRedirect><BuyerOrderAnalysisDetail /></ClientOnlyRedirect></Protected>} />
          <Route path="/ceo-command-center/client-requirements" element={<Protected><ClientOnlyRedirect><ClientRequirementsCompletion /></ClientOnlyRedirect></Protected>} />
          <Route path="/dashboards/:id" element={<Protected><ClientOnlyRedirect><DashboardView /></ClientOnlyRedirect></Protected>} />
          <Route path="/ms" element={<Protected><ClientOnlyRedirect><MsReport /></ClientOnlyRedirect></Protected>} />
          <Route path="/builder/:id" element={<Protected><ClientOnlyRedirect><Builder /></ClientOnlyRedirect></Protected>} />
          <Route path="/ai-agent" element={<Protected><ClientOnlyRedirect><AIAnalyticsAgent /></ClientOnlyRedirect></Protected>} />
          <Route path="/data-sources" element={<Protected><ClientOnlyRedirect><DataSources /></ClientOnlyRedirect></Protected>} />
          <Route path="/database-explorer" element={<Protected><ClientOnlyRedirect><DatabaseExplorer /></ClientOnlyRedirect></Protected>} />
          <Route path="/erp-flow-planner" element={<Protected><ClientOnlyRedirect><ERPFlowPlanner /></ClientOnlyRedirect></Protected>} />
          <Route path="/datasets" element={<Protected><ClientOnlyRedirect><Datasets /></ClientOnlyRedirect></Protected>} />
          <Route path="/sql" element={<Protected><ClientOnlyRedirect><SqlEditor /></ClientOnlyRedirect></Protected>} />
          <Route path="/reports" element={<Protected><ClientOnlyRedirect><Reports /></ClientOnlyRedirect></Protected>} />
          <Route path="/kpi" element={<Protected><ClientOnlyRedirect><KpiCenter /></ClientOnlyRedirect></Protected>} />
          <Route path="/exports" element={<Protected><ClientOnlyRedirect><Exports /></ClientOnlyRedirect></Protected>} />
          <Route path="/etl" element={<Protected><ClientOnlyRedirect><EtlAdmin /></ClientOnlyRedirect></Protected>} />
          <Route path="/uploads" element={<Protected><ClientOnlyRedirect><ExcelUploads /></ClientOnlyRedirect></Protected>} />
          <Route path="/audit" element={<Protected><ClientOnlyRedirect><AuditLogs /></ClientOnlyRedirect></Protected>} />
          <Route path="/workspaces" element={<Protected><ClientOnlyRedirect><Workspaces /></ClientOnlyRedirect></Protected>} />
          <Route path="/access-management" element={<Protected><ClientOnlyRedirect><AccessManagement /></ClientOnlyRedirect></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
